import React, { useState, useEffect } from 'react';
import fetchWithAuth from '../../../utils/fetchWithAuth';
import { RiUserFollowLine, RiUserUnfollowFill } from 'react-icons/ri';

interface FollowButtonProps {
  /** ID of the user to follow/unfollow */
  userId: string;
  /** Render mode: "text" for a text button, "icon" for an icon-only button */
  mode?: 'text' | 'icon';
  /** Optional className override for additional styling/positioning */
  className?: string;
  /** Controlled follow status (optional) */
  isFollowing?: boolean;
  onClick?: () => void;         // allow parent to override click
}

const FollowButton: React.FC<FollowButtonProps> = ({
  userId,
  mode = 'text',
  className = '',
  isFollowing: propIsFollowing,
  onClick,
}) => {
  // use controlled prop if given, otherwise fetch ourselves
  const [isFollowing, setIsFollowing] = useState<boolean>(!!propIsFollowing);

  // keep local state in sync when parent prop changes
  useEffect(() => {
    if (typeof propIsFollowing === 'boolean') {
      setIsFollowing(propIsFollowing);
    }
  }, [propIsFollowing]);

  // if uncontrolled, fetch initial status
  useEffect(() => {
    if (propIsFollowing === undefined) {
      (async () => {
        const res = await fetchWithAuth(`/api/users/${userId}/is-following`);
        setIsFollowing(res.isFollowing);
      })();
    }
  }, [userId, propIsFollowing]);

  const toggleFollow = async () => {
    await fetchWithAuth(
      `/api/users/${userId}/${isFollowing ? 'unfollow' : 'follow'}`,
      { method: 'POST' }
    );
    setIsFollowing((f) => !f);
  };

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (onClick) onClick(); else toggleFollow();
  };

  const Icon = isFollowing ? RiUserUnfollowFill : RiUserFollowLine;

  if (mode === 'icon') {
    return (
      <button
        onClick={handleClick}
        className={`${className} p-1 rounded hover:bg-gray-100`}
      >
        <Icon className="w-5 h-5 text-gray-700" />
      </button>
    );
  }

  // TEXT MODE: flex row, icon + text inline, icon inherits text-color
  return (
    <button
      onClick={handleClick}
      className={`${className} flex items-center space-x-2 px-2 py-2 text-sm rounded border
        ${
          isFollowing
            ? 'text-gray-600 border-gray-400 hover:bg-gray-100'
            : 'bg-primary text-white border-primary'
        }`}
    >
      <Icon className="w-5 h-5 text-current" />
      <span>{isFollowing ? 'Unfollow' : 'Follow'}</span>
    </button>
  );
};

export default FollowButton;
